import React, { RefObject, useEffect } from 'react';

type Options = {
    /**
     * A React Ref to the element of interest. Clicks outside this element will trigger a call to the given handler.
     */
    readonly element: RefObject<Node>;

    /**
     * Called when a click outside the element is detected.
     */
    readonly handler: () => void;

    /**
     * A ref to an element which is ignored (clicks to this element wont count as clicks outside)
     */
    readonly ignoreElement?: React.RefObject<Node>;

    /**
     * A Flag that determines if the handler should be triggered.
     */
    readonly enabled?: boolean;
};

export function useClickOutside({ element, handler, ignoreElement, enabled = true }: Options) {
    useEffect(() => {
        const listener = (event: TouchEvent | MouseEvent) => {
            if (!enabled) {
                return;
            }
            if (!element.current || element.current.contains(event.target as Node)) {
                return;
            }
            if (ignoreElement && ignoreElement.current && ignoreElement.current.contains(event.target as Node)) {
                return;
            }
            handler();
        };

        document.addEventListener('mousedown', listener);
        document.addEventListener('touchstart', listener);

        return () => {
            document.removeEventListener('mousedown', listener);
            document.removeEventListener('touchstart', listener);
        };
    }, [element, enabled, handler, ignoreElement]);
}
